import "./application.css"
import { createInertiaApp, router } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { Toaster, toast } from 'sonner'
import { TooltipProvider } from '@/components/ui/tooltip'

if (!import.meta.env.SSR) {
  // Self-heal stale Vite chunks after a deploy.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    if (navigator.onLine === false) return

    const key = 'endcap:preloadReloadAt'
    const now = Date.now()
    const last = Number(sessionStorage.getItem(key) || '0')
    if (now - last < 30_000) return

    sessionStorage.setItem(key, String(now))
    const url = new URL(window.location.href)
    url.searchParams.set('_v', String(now))
    window.location.replace(url.toString())
  })

  if (new URL(window.location.href).searchParams.has('_v')) {
    const cleaned = new URL(window.location.href)
    cleaned.searchParams.delete('_v')
    window.history.replaceState(window.history.state, '', cleaned.toString())
  }

  // Flash → Sonner toast
  router.on('flash', (event) => {
    const flash = (event as CustomEvent).detail.flash as Record<string, string | undefined>
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert, { duration: 6000 })
    if (flash?.info) toast(flash.info)
  })
}

void createInertiaApp({
  title: (title) => title ? `${title} - Endcap` : 'Endcap',
  pages: "../pages",

  setup({ el, App, props }) {
    const app = (
      <StrictMode>
        <Toaster position="top-center" richColors closeButton />
        <TooltipProvider delayDuration={150}>
          <App {...props} />
        </TooltipProvider>
      </StrictMode>
    )
    if (!el) return app
    if (el.hasChildNodes()) {
      hydrateRoot(el, app)
    } else {
      createRoot(el).render(app)
    }
    return app
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },
}).catch((error) => {
  if (typeof document !== 'undefined' && document.getElementById("app")) {
    throw error
  } else if (typeof document !== 'undefined') {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.",
    )
  } else {
    throw error
  }
})
